// The paywall upsell "plate" (plan 10 §4) — the locked-chapter view for a
// reader who isn't entitled. Styled as an end-of-volume subscription plate in
// the same printed-book language as the library (Alegreya display, body and
// numerals, let-spaced uppercase eyebrows, hairline rules): a
// wax-seal lock, the product name, the live price, and one clear call to
// action. The gated reader itself lives behind this in reading-view; this file
// has no reader dependency, so there is no import cycle.
//
// Reactive by construction: the entitlement is a live Convex query in the gate,
// so a completed purchase (this tab, the checkout return page, or another tab)
// flips straight through to the reader with no reload.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useAction, useQuery } from "convex/react";
import { ArrowLeft, Check, Layers, Loader2, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { api } from "@bindery/convex-retells/_generated/api";
import { AppNav } from "@/components/app-nav";
import { Button } from "@bindery/ui/components/ui/button";
import { errorMessage } from "@/lib/convex-error";
import { useLogger } from "@/logging";
import { signIn } from "@/lib/auth";
import { formatPrice } from "@/lib/price";
import { useCoarseNow } from "@/lib/use-coarse-now";
import { PriceAmount, SaleMark } from "./sale-price";
import { usePaywallImpression } from "./use-reading-telemetry";
import { useBuyProduct } from "./use-buy-product";
import { useBundleOffer } from "./use-bundle-offer";
import type { CatalogBook } from "@bindery/shared/corpus/catalog";
import { useLastLoaded } from "@/lib/use-last-loaded";
import type { EntitlementStatus, LockedUnit } from "./locked-unit";

/**
 * The locked-unit upsell. Rendered only when the viewer is NOT entitled; the
 * gate (reading-view) owns the live entitlement query and passes its result in.
 */
export function UpsellPlate({
  book,
  unit,
  status,
}: {
  book: CatalogBook;
  unit: LockedUnit;
  status: EntitlementStatus;
}) {
  const productId = unit.productId;
  // Coarse so the query's subscription key is stable across renders
  // (`useCoarseNow`); a sale window is the only reason this needs a clock.
  const now = useCoarseNow();
  // Held across the minute tick, so the price block doesn't unmount for a round
  // trip once a minute and shift the plate under the reader (`useLastLoaded`).
  const product = useLastLoaded(
    useQuery(api.checkout.getProduct, productId ? { productId, now } : "skip"),
  );

  // The complete-title bundle — id, price, and availability line, resolved by
  // the same hook the library shelf's bundle CTA uses so the two surfaces
  // cannot drift (`use-bundle-offer.ts`).
  const {
    bundleProductId,
    product: bundleProduct,
    availabilityNote,
  } = useBundleOffer(book, now);

  const confirmOrder = useAction(api.checkout.confirmOrder);
  const log = useLogger();
  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  // Where checkout returns the reader — right back on this chapter, which then
  // renders as unlocked once the reactive entitlement flips.
  const next = `/read/${book.slug}/${unit.segment}`;
  const { buy, busy: buying } = useBuyProduct(next);
  const [confirming, setConfirming] = useState(false);
  const busy = buying || confirming;

  // Record the paywall impression once per (book, chapter, auth state).
  useEffect(() => {
    logRef.current.event("paywall.view", {
      book: book.slug,
      // Omitted for a matter document, which has no chapter number —
      // `unit` carries the slug instead. The chapter field keeps its exact
      // meaning for the chapters that have always emitted it.
      chapter: unit.chapterNumber,
      unit: unit.segment,
      authed: status.authed,
    });
  }, [book.slug, unit.chapterNumber, unit.segment, status.authed]);

  // The same impression, stored rather than logged. The Axiom event above
  // answers "what happened"; this row is what the console can COUNT and
  // cross-tab — the top of the funnel, and the only part of it an anonymous
  // reader appears in.
  usePaywallImpression({
    bookSlug: book.slug,
    unitKey: unit.segment,
  });

  if (!productId) {
    // A premium chapter with no product config is a build/config bug, not a
    // user-facing state — degrade to a plain locked notice.
    return <LockedNotice book={book} message={unit.noticeLabel} />;
  }

  const price =
    product != null ? formatPrice(product.amountCents, product.currency) : null;
  const productName = product?.displayName ?? book.title;

  const bundlePrice =
    bundleProduct != null
      ? formatPrice(bundleProduct.amountCents, bundleProduct.currency)
      : null;

  const onComplete = async (): Promise<void> => {
    if (status.pendingOrderId === null) return;
    setConfirming(true);
    try {
      const result = await confirmOrder({ orderId: status.pendingOrderId });
      switch (result.kind) {
        case "granted":
        case "already_granted":
          toast.success("Purchase confirmed", {
            description: "Your books are unlocked.",
          });
          break; // the reactive entitlement flips the gate to the reader
        case "processing":
          toast("Still processing", {
            description: "Your purchase will appear shortly.",
          });
          break;
        case "pending":
        case "expired":
          toast("Let's try that again", {
            description: "Your previous checkout didn't complete.",
          });
          await buy(productId);
          break;
        case "failed":
          toast.error("Couldn't confirm your purchase", {
            description: "Please try again.",
          });
          break;
      }
    } catch (error) {
      toast.error("Couldn't confirm your purchase", {
        description: errorMessage(error, "Please try again."),
      });
    } finally {
      setConfirming(false);
    }
  };

  const onSignIn = (): void => {
    logRef.current.event("paywall.signin", {
      book: book.slug,
      chapter: unit.chapterNumber,
      unit: unit.segment,
    });
    signIn();
  };

  return (
    <PlateFrame book={book}>
      <p className="text-muted-foreground font-serif text-xs tracking-[0.35em] uppercase">
        {book.title} · Locked
      </p>

      {/* Wax-seal lock. */}
      <div
        aria-hidden="true"
        className="border-border bg-card text-muted-foreground mt-8 flex size-16 items-center justify-center rounded-full border shadow-sm"
      >
        <LockKeyhole className="size-6" />
      </div>

      <h1 className="text-foreground mt-8 max-w-xl font-serif text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
        {productName}
      </h1>
      {/*
        Deliberately says "this chapter", not "the second half": the free set is
        no longer a contiguous head. A multi-volume book opens each of its later
        volumes with a free chapter, so a reader can arrive here at chapter 6 or
        at chapter 14, and a copy line naming a halfway point would be wrong in
        both places.
      */}
      <p className="text-muted-foreground mt-5 max-w-md font-serif text-lg leading-relaxed text-pretty">
        This chapter is part of {productName}. Unlock it to keep reading, side
        by side with the original — no subscription.
      </p>

      {product != null && (
        <div className="mt-8 flex flex-col items-center gap-2">
          <SaleMark offer={product} now={now} />
          <PriceAmount offer={product} emphasis="plate" />
        </div>
      )}

      <div className="mt-8 flex flex-col items-center gap-3">
        {!status.authed ? (
          <>
            <Button size="lg" onClick={onSignIn} className="min-w-56">
              Sign in to unlock
            </Button>
            <p className="text-muted-foreground font-serif text-sm">
              Already purchased?{" "}
              <button
                type="button"
                onClick={onSignIn}
                className="text-primary underline underline-offset-4"
              >
                Sign in
              </button>{" "}
              to restore it.
            </p>
          </>
        ) : status.pendingOrderId !== null ? (
          <>
            <Button
              size="lg"
              onClick={() => void onComplete()}
              disabled={busy}
              className="min-w-56"
            >
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Check className="size-4" />
              )}
              Complete your purchase
            </Button>
            <button
              type="button"
              onClick={() => void buy(productId)}
              disabled={busy}
              className="text-muted-foreground font-serif text-sm underline underline-offset-4 disabled:opacity-50"
            >
              Start a new checkout instead
            </button>
          </>
        ) : (
          <Button
            size="lg"
            onClick={() => void buy(productId)}
            disabled={busy}
            className="min-w-56"
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            {price !== null ? `Unlock for ${price}` : "Unlock"}
          </Button>
        )}
      </div>

      <p className="text-muted-foreground mt-6 font-serif text-xs tracking-wide">
        One-time purchase · secure checkout at checkout.sara.ai
      </p>

      {/*
        The bundle CTA — restores the "buy it all in one purchase"
        convenience the over-grant bug used to give away by accident, now
        correctly scoped: this buys every paywalled volume of the title, not
        just the one this chapter belongs to. Only rendered when the config
        actually names a bundle product (`legacyBundleProductId`) — a
        multi-volume title with no bundle minted yet gets no invented CTA.
      */}
      {bundleProductId && (
        <div className="border-border mt-8 flex flex-col items-center gap-3 border-t pt-8">
          <p className="text-muted-foreground font-serif text-sm">
            Reading through {book.title}?
          </p>
          {/*
            Sign-in FIRST for an anonymous reader, exactly as the primary CTA
            above and the library's own bundle line do. `createSession` starts
            with `requireUser`, so buying without an identity throws
            `unauthenticated` and the reader gets that machine tag in a red
            toast with nothing to click — a dead end reached from the same
            plate whose main button correctly says "Sign in to unlock".
          */}
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              if (!status.authed) {
                onSignIn();
                return;
              }
              void buy(bundleProductId);
            }}
            disabled={busy}
            className="min-w-56"
          >
            <Layers className="size-4" />
            {bundlePrice !== null
              ? `Unlock every volume — ${bundlePrice}`
              : "Unlock every volume at once"}
          </Button>
          {availabilityNote && (
            <p className="text-muted-foreground max-w-xs font-serif text-xs">
              {availabilityNote}
            </p>
          )}
        </div>
      )}

      {/*
        The other way in. This plate sells ONE book forever; all-access rents
        the whole shelf. A reader staring at a locked chapter is exactly the
        person for whom that trade is worth naming — and before this, the
        membership page could only be found by exhausting a translation
        allowance, which a reader who never opens the gloss popover never does.
      */}
      <p className="border-border text-muted-foreground mt-10 border-t pt-6 font-serif text-sm">
        Reading more than one?{" "}
        <Link
          to={`/subscribe?next=${encodeURIComponent(next)}`}
          className="text-primary underline underline-offset-4"
        >
          All access unlocks every book
        </Link>{" "}
        for a monthly subscription.
      </p>
    </PlateFrame>
  );
}

/** The framed, centered plate layout shared by the upsell + the fallback notice. */
function PlateFrame({
  book,
  children,
}: {
  book: CatalogBook;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-background min-h-svh">
      <header className="border-border flex items-center gap-2 border-b px-3 py-2">
        <Button
          asChild
          variant="ghost"
          size="icon-sm"
          aria-label="Back to library"
        >
          <Link to="/">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <p className="text-foreground min-w-0 flex-1 truncate font-serif text-sm font-bold">
          {book.title}
        </p>
        {/* This plate renders INSIDE the `/read` route, which owns its chrome
            (App.tsx) — so the site controls belong to this header, not to a
            floating cluster that would land on top of it. */}
        <AppNav variant="inline" />
      </header>
      <main className="mx-auto flex max-w-2xl flex-col items-center px-6 pt-16 pb-24 text-center sm:pt-24">
        {children}
      </main>
    </div>
  );
}

/** A plain locked message (config error / no product) — no purchase affordance. */
function LockedNotice({
  book,
  message,
}: {
  book: CatalogBook;
  message: string;
}) {
  return (
    <PlateFrame book={book}>
      <div
        aria-hidden="true"
        className="border-border bg-card text-muted-foreground flex size-16 items-center justify-center rounded-full border"
      >
        <LockKeyhole className="size-6" />
      </div>
      <p className="text-muted-foreground mt-8 font-serif">{message}</p>
      <Link
        to="/"
        className="text-primary mt-4 font-serif text-sm underline underline-offset-4"
      >
        Back to the library
      </Link>
    </PlateFrame>
  );
}
